import {getMailbox} from "./get-mailbox.ts";

/**
 * Manage sending messages to the relevant integration.
 *
 * This looks for a mailbox and posts messages to it if it is present;
 * otherwise, it maintains a queue of posted messages and waits for a mailbox to
 * be present.
 *
 * We maintain this queue because some integrations may take time to setup their
 * mailbox endpoint. For example, there is a long-standing bug where the
 * `window.ReactNativeWebView` object is not present when the page first loads,
 * and we suspect that this is not fully fixed (despite the GitHub issue trail
 * possibly indicating otherwise). So, to mitigate that we must poll for
 * `window.ReactNativeWebView`.
 */
export class SendQueue {
    private _intervalId: number | undefined = undefined;
    private _postMessageQueue: Array<string> = [];

    /**
     * Get a copy of the current queue.
     */
    get queue(): ReadonlyArray<string> {
        return [...this._postMessageQueue];
    }

    /**
     * Attempt to flush the message queue.
     *
     * @returns `true` if the queue was flushed; `false` otherwise.
     */
    private _tryFlushQueue(): boolean {
        const mailbox = getMailbox();
        if (!mailbox?.canSend) {
            return false;
        }

        // We can't use Wonder Blocks Timing here.
        // eslint-disable-next-line no-restricted-syntax -- see above
        window.clearInterval(this._intervalId);
        this._intervalId = undefined;
        for (const message of this._postMessageQueue) {
            mailbox.send(message);
        }
        this._postMessageQueue.length = 0;
        return true;
    }

    /**
     * Retry flushing the queue on an interval.
     *
     * This sets up an interval to try flushing the queue every 500ms. The
     * interval is cancelled when the queue can be flushed.
     */
    private _retryFlushQueueOnInterval(): void {
        if (this._intervalId != null) {
            return;
        }

        // We can't use Wonder Blocks Timing here.
        // eslint-disable-next-line no-restricted-syntax -- see above
        this._intervalId = window.setInterval(() => this._tryFlushQueue(), 500);
    }

    /**
     * Queue a message to be sent to the integration app.
     *
     * This queues the given message and ensures that we have setup our interval
     * for flushing the queue.
     */
    private _queueMessage(message: string): void {
        this._postMessageQueue.push(message);
        this._retryFlushQueueOnInterval();
    }

    /**
     * Post or queue a message to be sent to the integration app.
     */
    postOrQueueMessage(message: string): void {
        // If we successfully flush the queue, we are good to go and we can post
        // the message; otherwise, we need to queue the message and wait for a
        // mailbox to be present.
        if (this._tryFlushQueue()) {
            // We can assume that if we flushed the queue we will have a
            // mailbox.
            const mailbox = getMailbox();
            mailbox!.send(message);
        } else {
            this._queueMessage(message);
        }
    }
}
